import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold">Item History</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <div className="p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

const ItemCard = ({ item, className, userId, timestamp }) => (
  <div className={`p-4 rounded mb-2 ${className}`}>
    <div className="mb-2 text-sm text-gray-600">
      <span className="font-medium">User ID: {userId}</span>
      {timestamp && (
        <span className="ml-4">
          {format(new Date(timestamp), 'MMM d, yyyy HH:mm:ss')}
        </span>
      )}
    </div>
    <p>Item Name: {item?.itemName || 'N/A'}</p>
    <p>Quantity: {item?.quantity || 0}</p>
    <p>Stock: {item?.stock || 0}</p>
    {item?.description && <p>Change: {item.description}</p>}
  </div>
);

const IndentChangesTracker = ({
  indentID,
  changes = { addedItems: [], deletedItems: [], updatedItems: [] },
  storedChangeLogs = []
}) => {
  const processStoredLogs = () => {
    const processed = {
      addedItems: [],
      deletedItems: [],
      updatedItems: []
    };

    storedChangeLogs.forEach(log => {
      if (!log) return;

      try {
        switch (log.change_type) {
          case 'add':
            if (log.item_data) {
              processed.addedItems.push({
                ...JSON.parse(typeof log.item_data === 'string' ? log.item_data : JSON.stringify(log.item_data)),
                userId: log.user_id,
                timestamp: log.created_at
              });
            }
            break;
          case 'delete':
            if (log.item_data) {
              processed.deletedItems.push({
                ...JSON.parse(typeof log.item_data === 'string' ? log.item_data : JSON.stringify(log.item_data)),
                userId: log.user_id,
                timestamp: log.created_at
              });
            }
            break;
          case 'update':
            if (log.original_data && log.updated_data) {
              processed.updatedItems.push({
                original: JSON.parse(typeof log.original_data === 'string' ? log.original_data : JSON.stringify(log.original_data)),
                updated: JSON.parse(typeof log.updated_data === 'string' ? log.updated_data : JSON.stringify(log.updated_data)),
                userId: log.user_id,
                timestamp: log.created_at
              });
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('Error processing log:', error);
      }
    });

    return processed;
  };

  const processedLogs = processStoredLogs();
  const allChanges = {
    addedItems: [...(changes.addedItems || []), ...processedLogs.addedItems],
    deletedItems: [...(changes.deletedItems || []), ...processedLogs.deletedItems],
    updatedItems: [...(changes.updatedItems || []), ...processedLogs.updatedItems]
  };

  const hasChanges = Object.values(allChanges).some(items => items.length > 0);

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <div className="bg-white">
        <h2 className="text-xl font-bold mb-4">Indent Changes - {String(indentID)}</h2>

        {!hasChanges && <p className="text-gray-500">No changes to display.</p>}

        {allChanges.addedItems.length > 0 && (
          <section className="mb-6">
            <h3 className="font-semibold text-green-600 mb-3">Added Items</h3>
            {allChanges.addedItems.map((item, index) => (
              <ItemCard 
                key={`added-${index}`} 
                item={item} 
                className="bg-green-50"
                userId={item.userId}
                timestamp={item.timestamp}
              />
            ))}
          </section>
        )}

        {allChanges.deletedItems.length > 0 && (
          <section className="mb-6">
            <h3 className="font-semibold text-red-600 mb-3">Deleted Items</h3>
            {allChanges.deletedItems.map((item, index) => (
              <ItemCard 
                key={`deleted-${index}`} 
                item={item} 
                className="bg-red-50"
                userId={item.userId}
                timestamp={item.timestamp}
              />
            ))}
          </section>
        )}

        {allChanges.updatedItems.length > 0 && (
          <section>
            <h3 className="font-semibold text-blue-600 mb-3">Updated Items</h3>
            {allChanges.updatedItems.map((update, index) => (
              <div
                key={`updated-${index}`}
                className="bg-blue-50 p-4 rounded mb-2"
              >
                <div className="mb-2 text-sm text-gray-600">
                  <span className="font-medium">User ID: {update.userId}</span>
                  {update.timestamp && (
                    <span className="ml-4">
                      {format(new Date(update.timestamp), 'MMM d, yyyy HH:mm:ss')}
                    </span>
                  )}
                </div>
                <p>Item: {update.original?.itemName || 'N/A'}</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p>Original Quantity: {update.original?.quantity || 0}</p>
                    <p>Original Stock: {update.original?.stock || 0}</p>
                  </div>
                  <div>
                    <p className="text-green-600">
                      Updated Quantity: {update.updated?.quantity || 0}
                    </p>
                    <p className="text-green-600">
                      Updated Stock: {update.updated?.stock || 0}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

const ItemHistoryButton = ({ indentID, changes, storedChangeLogs }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Item History
      </button>
      
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <IndentChangesTracker
          indentID={indentID}
          changes={changes}
          storedChangeLogs={storedChangeLogs}
        />
      </Modal>
    </>
  );
};

ItemHistoryButton.propTypes = {
  indentID: PropTypes.string.isRequired,
  changes: PropTypes.shape({
    addedItems: PropTypes.arrayOf(PropTypes.object),
    deletedItems: PropTypes.arrayOf(PropTypes.object),
    updatedItems: PropTypes.arrayOf(PropTypes.object)
  }),
  storedChangeLogs: PropTypes.arrayOf(PropTypes.object)
};

export default ItemHistoryButton;